import { toast } from "react-toastify";
import { handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../../components/common/DeleteModal";

import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import { useState } from "react";
import Search from "../../../../components/form/Search";
import Table from "../../../../components/elements/table/Table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import ActionBar from "../../../../components/elements/ActionBar";
import { lngDetect } from "../../../../helpers/lngDetect";
import Badge from "../../../../components/elements/Badge";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import ManageArtistDrawer from "../../../../components/sections/front/artist/drawer/ManageArtistDrawer";
import { artistTeamStatusChange, organizationTeamList } from "../../../../services/organizationService";
import { t } from "i18next";

const Artist = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const {
    data: artist,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["artist", filters, search],
    queryFn: async () => {
      const res = await organizationTeamList({ type: "artist-verification", ...filters, ...(search && { keyword: search }) });
      return res?.docs;
    },
  });

  const tableHeadData = [
    {
      _id: "Artist",
      name: "Artist",
      align: "left",
    },

    {
      _id: "profileData",
      name: "Bio",
      align: "left",
    },

    {
      _id: 5,
      name: "Status",
      align: "left",
    },
    {
      _id: 8,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = artist?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "user",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.info?.displayName || "-",
          image: item?.info?.avatarUrl,
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: <div className="line-clamp-2">{item?.info?.profileData || "-"}</div>,
        },

        {
          _id: 6,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: <Badge label={item?.status === "active" ? "accepted" : "pending"} type={item?.status === "active" ? "success" : "danger"} />,
        },
        ...(item?.status === "pending"
          ? [
              {
                _id: "action",
                type: "action",
                align: "left",
                cellClass: "w-[5%]",
                actionData: [
                  {
                    _id: "status change",
                    name: t("Accept"),
                    icon: "fa-regular fa-chart-candlestick",
                    onClick: () => {
                      statusChangeHandler({ id: item?._id });
                    },
                    standout: false,
                  },
                ],
              },
            ]
          : [
              {
                _id: "action",
                type: "text",
                align: "left",
                cellClass: "w-[5%]",
              },
            ]),
      ],
    };
  });

  //   artist status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } = useMutation({
    mutationFn: async ({ id, status }) => {
      queryClient.setQueryData(["artist", filters, search], (state) => {
        if (state) {
          let stateData = JSON.parse(JSON.stringify(state?.data || []));
          stateData = (stateData || []).map((elm) => (elm?._id === id ? { ...elm, status: "active" } : elm));
          return { ...state, data: stateData };
        }
      });
      const res = await artistTeamStatusChange({
        id: id,
        status: "active",
      });
      return { res, payload: { id, status: "active" } };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status !== 200) {
        queryClient.setQueryData(["artist", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || []).map((elm) => (elm?._id === payload?.id ? { ...elm, status: "pending" } : elm));
            return { ...state, data: stateData };
          }
        });
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  // add or edit page =================>
  function orderManageResponse({ res, status }) {
    if (res?.status === 200 || res?.status === 201) {
      refetch();
      // setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  //   Order delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {},
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(["order", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.filter((item) => !payload?.includes(item?._id));

            return { ...state, data: stateData };
          }
        });

        if (artist?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["order", filters, search]);
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const options = [
    {
      id: "userDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "User Delete",
    },
  ];

  function actionHandler(val) {
    switch (val) {
      case "userDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }

  const { mutate: handleOrdering, isPending } = useMutation({
    mutationFn: async ({ newData, oldData }) => {},
    onSuccess: ({ res, newData, oldData }) => {
      if (res?.status !== 200) {
        queryClient.setQueryData(["order", filters, search], (state) => {
          if (state) {
            return { ...state, data: oldData };
          }
        });
        toast.warning(res?.message || res?.error);
      }
    },
  });

  return (
    <div className="w-full flex flex-col pb-10 bg-white rounded-md">
      <div className="space-y-8 py-8 px-5">
        <div className="flex justify-between items-center border-b border-gray-300">
          <div className="flex flex-col gap-2 w-full">
            <div className="font-bold text-lg xl:text-2xl uppercase tracking-tighter font-Montserrat">Artist Management</div>
            <div className="text-sm xl:text-base h-10">
              Easily handle all artist's actions: update artist's info, status, and add new artist's data.
            </div>
          </div>
        </div>
        <div className="flex items-center justify-start gap-3">
          <div className="w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={artist?.metadata?.totalDocs}
              onChange={(val) => handleFilterChange("limit", val, setFilters)}
            />
          </div>
          <div className="relative w-72">
            <Search
              placeholder={"Search here"}
              // divClasses={"!border-slate-300 !bg-slate-50"}
              search={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
          </div>
          {selectedItems?.length > 0 && (
            <div className="flex items-center gap-2">
              <ActionBar options={options} onChange={actionHandler} />
              <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
                {selectedItems?.length} Selected
                <button onClick={() => setSelectedItems([])} type="button" className="cursor-pointer">
                  <i className="fa-fw fa-light fa-xmark"></i>
                </button>
              </span>
            </div>
          )}
        </div>
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          pageCount={artist?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          originalData={[...(artist?.data || [])]}
          setOriginalData={(val) => {
            queryClient.setQueryData(["order", filters, search], (state) => {
              if (state) {
                return { ...state, data: val };
              }
            });
          }}
          handleOrdering={(newData, oldData) => handleOrdering({ newData, oldData })}
          //   isOrderingDisabled={false}
          isFetching={isFetching}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
          // selectable
        />
      </div>
      <ManageArtistDrawer
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={orderManageResponse}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default Artist;
